// Kiid components' only connection to this theme (Broadcast). Components call window.kiidAdapter
// instead of touching theme internals, so a theme change only needs edits here.
//
//   kiidAdapter.formatMoney(cents, format)   Shopify money format string -> text
//   kiidAdapter.addItems(items, options)     items: [{ id, quantity, properties? }]; resolves on success,
//                                            rejects with an Error whose message is safe to show shoppers.
//
// addItems hands the request to the theme's own add to cart handling (the "theme:cart:add" event), so the
// cart drawer, loading state and cart count behave exactly like the theme's normal add to cart button.
// If the theme's handler isn't available it falls back to /cart/add.js and sends the shopper to the cart.

if (!window.kiidAdapter) {
  const ADD_TIMEOUT_MS = 15000;

  const appendItemFields = (form, items) => {
    const add = (name, value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };
    items.forEach((item, index) => {
      add(`items[${index}][id]`, item.id);
      add(`items[${index}][quantity]`, item.quantity);
      if (item.selling_plan) add(`items[${index}][selling_plan]`, item.selling_plan);
      Object.entries(item.properties || {}).forEach(([key, value]) => {
        add(`items[${index}][properties][${key}]`, value);
      });
    });
  };

  const addDirect = async (items) => {
    const response = await fetch(`${window.theme?.routes?.cart_add_url || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message);
    window.location = window.theme?.routes?.cart_url || '/cart';
    return data;
  };

  window.kiidAdapter = {
    formatMoney(cents, format) {
      const value = (cents / 100).toFixed(2);
      const [whole, decimals] = value.split('.');
      const withDelimiter = (delimiter) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, delimiter);
      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, placeholder) => {
        switch (placeholder) {
          case 'amount_no_decimals':
            return withDelimiter(',');
          case 'amount_with_comma_separator':
            return `${withDelimiter('.')},${decimals}`;
          case 'amount_no_decimals_with_comma_separator':
            return withDelimiter('.');
          default:
            return `${withDelimiter(',')}.${decimals}`;
        }
      });
    },

    addItems(items, { container = document.body } = {}) {
      if (!window.theme?.routes?.cart_add_url) return addDirect(items);

      return new Promise((resolve, reject) => {
        const form = document.createElement('form');
        form.hidden = true;
        form.action = window.theme.routes.cart_add_url;
        form.method = 'post';
        appendItemFields(form, items);

        const button = document.createElement('button');
        button.type = 'submit';
        form.appendChild(button);
        container.appendChild(form);

        const finish = (callback, value) => {
          window.clearTimeout(timer);
          button.removeEventListener('theme:product:add', onAdd);
          button.removeEventListener('theme:product:add-error', onError);
          form.remove();
          callback(value);
        };
        const onAdd = (event) => finish(resolve, event.detail?.response);
        const onError = (event) => {
          const response = event.detail?.response || {};
          finish(reject, new Error(response.description || response.message || 'Could not add to cart.'));
        };
        const timer = window.setTimeout(() => finish(reject, new Error('Adding to cart took too long. Please try again.')), ADD_TIMEOUT_MS);

        button.addEventListener('theme:product:add', onAdd);
        button.addEventListener('theme:product:add-error', onError);
        document.dispatchEvent(new CustomEvent('theme:cart:add', { detail: { button } }));
      });
    },
  };
}
